// components/ProjectTemplate.jsx
import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import rehypeSlug from "rehype-slug";
import rehypeAutolinkHeadings from "rehype-autolink-headings";
import "katex/dist/katex.min.css"; // KaTeX styles for math rendering
import ProjectFooter from "../pages/ProjectFooter";
import OptimizedImage from "./OptimizedImage";
import "../styles/ProjectTemplate.css";
import GitHubLogo from "../images/projects/github.png";
import { languageIcons } from "../data/projectsData";

const ProjectTemplate = ({ project, screenshots = [] }) => {
  if (!project) {
    return <div>Project not found</div>;
  }

  // Use 'content' field for full markdown, fall back to 'description' for backwards compatibility
  const markdownContent = project.content || project.description;

  return (
    <>
      <title>{project.name} - Arthur Efremenko</title>
      <meta name="description" content={project.description} />
      <div className="template-project-container">
        <div className="template-project-header">
          <OptimizedImage
            src={project.logoRef}
            alt={project.logoAlt}
            className="template-project-logo"
            eager
          />
          <h1 className="template-project-name">{project.name}</h1>

          <div className="template-project-languages">
            {project.languages.map((language) => {
              const imgAlt = `${language} programming language logo`;
              return (
                <OptimizedImage
                  key={language}
                  src={languageIcons[language]}
                  alt={imgAlt}
                  className="template-language-img"
                  eager
                />
              );
            })}
          </div>

          <a
            className="template-github-ref"
            href={project.gitHubRef}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`View ${project.name} on GitHub`}
          >
            <OptimizedImage
              className="template-github-img"
              src={GitHubLogo}
              alt="GitHub logo"
              eager
            />
          </a>
        </div>

        <div className="template-project-description">
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkMath]}
            rehypePlugins={[rehypeKatex, rehypeSlug, rehypeAutolinkHeadings]}
            components={{
              // Custom link rendering
              a: ({ node, children, href, ...props }) => {
                // Check if it's an internal reference link (starts with #)
                if (href && href.startsWith("#")) {
                  return (
                    <a href={href} className="reference-link" {...props}>
                      {children}
                    </a>
                  );
                }
                // External links open in new tab
                return (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    {...props}
                  >
                    {children}
                  </a>
                );
              },
              // Custom image rendering for responsive images
              img: ({ node, ...props }) => (
                <OptimizedImage
                  {...props}
                  className="markdown-image"
                  alt={props.alt || "Markdown attachment"}
                />
              ),
              // Custom code block rendering
              code: ({ node, inline, className, children, ...props }) => {
                const match = /language-(\w+)/.exec(className || "");
                return !inline ? (
                  <pre
                    className={`code-block ${match ? `language-${match[1]}` : ""}`}
                  >
                    <code className={className} {...props}>
                      {children}
                    </code>
                  </pre>
                ) : (
                  <code className="inline-code" {...props}>
                    {children}
                  </code>
                );
              },
            }}
          >
            {markdownContent}
          </ReactMarkdown>
        </div>

        {screenshots.length > 0 && (
          <div className="template-app-screenshot-container">
            {screenshots.map((screenshot, index) => (
              <OptimizedImage
                key={index}
                className="template-app-screenshot"
                src={screenshot.src}
                alt={screenshot.alt}
              />
            ))}
          </div>
        )}

        <div className="template-project-footer">
          <ProjectFooter />
        </div>
      </div>
    </>
  );
};

export default ProjectTemplate;
